/**
 * PRD.270 — Walk-in services → invoice import: pure logic.
 *
 * Dependency-free (no external imports) so eligibility / remaining-quantity /
 * mapping / ordering rules are unit-testable with node:test. The QuerySpec that
 * feeds these lives in `walk-in-services.ts`.
 *
 * A "walk-in" is a `queue_entries` row with `source = 'walk-in'`. Each entry
 * references at most ONE appointment service (`service_id`), quantity implicitly
 * 1 — there is no per-service child table today. Duplicate prevention keys on
 * the stable source triple (org, source_id = queue entry id, service_id) with a
 * deterministic invoice-item id so concurrent/retried imports converge.
 */

/** invoice_items.source_type value for a walk-in import. */
export const WALK_IN_SOURCE_TYPE = 'walk_in';
/** queue_entries.source value that marks a walk-in. */
export const WALK_IN_QUEUE_SOURCE = 'walk-in';
/**
 * Queue statuses a walk-in service can be billed from — the active arrived/served
 * states. Uses the real queue enum ('expected' | 'waiting' | 'called' |
 * 'serving' | 'done'). Excluded:
 *   - 'expected' — not yet arrived.
 *   - 'done' — the queue entry is closed/completed; a closed queue means the
 *     visit is settled, so its walk-in services should no longer be suggested
 *     for billing (mirrors the closed-consultation exclusion for prescriptions
 *     and used supplies). Anything already invoiced is filtered separately by
 *     invoiced_count, so this only stops closed-but-unbilled entries lingering.
 * (There is no 'cancelled' status — entries are hard-deleted.)
 */
export const WALK_IN_ELIGIBLE_STATUSES = ['waiting', 'called', 'serving'] as const;

/**
 * Deterministic invoice_items.id for a walk-in service import. Because the id is
 * derived from the source, two clients importing the same walk-in service (or an
 * upload retry) produce the SAME id and converge through `ON CONFLICT (id) DO
 * UPDATE` at the sync-write layer — no duplicate row, and the partial unique
 * index `ii_walkin_source_uidx` is never actually violated.
 */
export function walkInInvoiceItemId(queueEntryId: string, serviceId: string): string {
  return `wi-${queueEntryId}-${serviceId}`;
}

/** Raw row from `qEligibleWalkInServices`. */
export interface WalkInServiceCandidateRow {
  queue_entry_id: string;
  queue_number: string | null;
  queue_date: string | null;
  status: string | null;
  clinic_id: string | null;
  service_id: string | null;
  provider_id: string | null;
  provider_name: string | null;
  service_name: string | null;
  price_cents: number | null;
  /** Active invoice lines already backing this walk-in service. */
  invoiced_count: number | null;
}

/** A walk-in service candidate with derived billing fields for the picker. */
export interface WalkInServiceVM {
  queueEntryId: string;
  queueNumber: string | null;
  queueDate: string | null;
  status: string | null;
  clinicId: string | null;
  serviceId: string;
  serviceName: string;
  providerName: string | null;
  unitPrice: number;
  /** Source quantity — always 1 today (one service per walk-in). */
  sourceQuantity: number;
  invoicedQuantity: number;
  remainingQuantity: number;
  eligible: boolean;
}

/**
 * Derive the picker view-model for one candidate: unit price from the
 * appointment-service catalog (`price_cents / 100`), and remaining =
 * sourceQuantity - already-invoiced. A row with no service_id, or fully
 * invoiced, is not eligible for selection.
 */
export function toWalkInServiceVM(row: WalkInServiceCandidateRow): WalkInServiceVM | null {
  if (!row.service_id) return null;
  const sourceQuantity = 1; // one service per walk-in (PRD.270 data model)
  const invoicedQuantity = Math.max(0, Number(row.invoiced_count ?? 0));
  const remainingQuantity = Math.max(0, sourceQuantity - invoicedQuantity);
  return {
    queueEntryId: row.queue_entry_id,
    queueNumber: row.queue_number,
    queueDate: row.queue_date,
    status: row.status,
    clinicId: row.clinic_id,
    serviceId: row.service_id,
    serviceName: row.service_name ?? 'Service',
    providerName: row.provider_name,
    unitPrice: (row.price_cents ?? 0) / 100,
    sourceQuantity,
    invoicedQuantity,
    remainingQuantity,
    eligible: remainingQuantity > 0,
  };
}

/**
 * Order candidates for the picker (PRD.270 default ordering):
 *   1. the walk-in linked to the current invoice visit/encounter (preferred id)
 *   2. walk-ins from today
 *   3. most-recent walk-in date, then queue number
 */
export function sortWalkInServiceVMs(
  rows: readonly WalkInServiceVM[],
  opts: { preferredQueueEntryId?: string | null; today?: string | null } = {},
): WalkInServiceVM[] {
  const { preferredQueueEntryId = null, today = null } = opts;
  const rank = (r: WalkInServiceVM): number => {
    if (preferredQueueEntryId && r.queueEntryId === preferredQueueEntryId) return 0;
    if (today && r.queueDate === today) return 1;
    return 2;
  };
  return [...rows].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    // Within the same rank: most recent date first, then queue number.
    const da = a.queueDate ?? '';
    const db = b.queueDate ?? '';
    if (da !== db) return da < db ? 1 : -1;
    return (a.queueNumber ?? '').localeCompare(b.queueNumber ?? '');
  });
}
